import { useMemo } from 'react';

import { JsonObject } from '@suntex.core/types';
import {
  Database_Tables_ModuleTag,
  Entities_Modules_Module,
  Entities_Modules_ModuleModuleTag,
} from '@suntex.fim/api-client';
import { getFlags } from '@suntex.fim/flags';

import { useFramework } from './useFramework';
import { useModules } from './useModules';
import { useSessionDataLoader } from './useSessionDataLoader';

export type ModuleTagCategory =
  | 'FocusMode'
  | 'GameCollection'
  | 'StagesOfLearning'
  | 'MathObjectives'
  | 'Status'
  | (string & {});

export type ModuleTag = {
  category: ModuleTagCategory;
  path: string;
  metaData?: object;
};

export type ModuleSpecifier = {
  slug: string;
  variant: number;
};

export type ModuleTagging = {
  tag: ModuleTag;
  module: ModuleSpecifier;
  metaData?: {
    DisplayOrder?: number;
    DifficultyLevel?: number;
  };
};

interface ModuleTaggingData {
  moduleTags: Database_Tables_ModuleTag[];
  moduleModuleTags: Entities_Modules_ModuleModuleTag[];
}

interface ModuleTaggingResult {
  moduleTagging: ModuleTagging[] | undefined;
  moduleTags: Database_Tables_ModuleTag[] | undefined;
  isLoading: boolean;
}

// Well-known tag paths for game collections
export const GAME_COLLECTION_TAG_PATHS = {
  algebraReadiness: 'algebra-readiness',
  fractionsFest: 'fractions-fest',
  topGames: 'top-games',
  firstInReading: 'first-in-reading',
} as const;

/**
 * Tag category and path used to mark a module (game) as "under construction",
 * i.e. temporarily unavailable. Driven entirely by module tagging data so a
 * game can be taken offline by tagging its DB row, with no code deploy. The
 * dedicated `Status` category leaves room for future statuses (beta,
 * deprecated, etc.).
 */
export const MODULE_STATUS_CATEGORY = 'Status';
export const UNDER_CONSTRUCTION_TAG_PATH = 'under-construction';

/**
 * Parse tag metadata JSON string.
 * Returns undefined if the string is empty or invalid JSON.
 */
function parseTagMetaData(
  metaData: string | null | undefined,
): object | undefined {
  if (!metaData) {
    return undefined;
  }
  try {
    return JSON.parse(metaData) as object;
  } catch {
    // Invalid JSON, return undefined
    return undefined;
  }
}

/**
 * Slug prefix for modules that should always be treated as under construction,
 * regardless of any DB tagging. Used for in-progress modules that aren't
 * ready to be surfaced to players yet.
 */

const VIF_SLUG_PREFIX = 'vif-';

function buildSyntheticUnderConstructionTagging(
  modules: Entities_Modules_Module[],
): ModuleTagging[] {
  const flags = getFlags();
  return modules
    .filter((m) => m.slug.startsWith(VIF_SLUG_PREFIX) && !flags.modules.vif)
    .map((m) => ({
      tag: {
        category: MODULE_STATUS_CATEGORY,
        path: UNDER_CONSTRUCTION_TAG_PATH,
      },
      module: {
        slug: m.slug,
        variant: 0,
      },
    }));
}

function transformToModuleTagging(
  moduleTags: Database_Tables_ModuleTag[],
  moduleModuleTags: Entities_Modules_ModuleModuleTag[],
  modules: Entities_Modules_Module[],
): ModuleTagging[] {
  const moduleIdToSlug = new Map<number, string>();
  for (const m of modules) {
    if (m.slug) {
      moduleIdToSlug.set(m.id, m.slug);
    }
  }

  // Pre-parse all tag metadata once
  const tagIdToTag = new Map<
    number,
    { tag: Database_Tables_ModuleTag; parsedMetaData: object | undefined }
  >();
  for (const t of moduleTags) {
    tagIdToTag.set(t.id, {
      tag: t,
      parsedMetaData: parseTagMetaData(t.metaData),
    });
  }

  const result: ModuleTagging[] = [];

  for (const mmt of moduleModuleTags) {
    if (mmt.moduleId === undefined || mmt.moduleTagId === undefined) {
      continue;
    }

    const tagEntry = tagIdToTag.get(mmt.moduleTagId);
    const slug = moduleIdToSlug.get(mmt.moduleId);

    if (!tagEntry || !slug) {
      continue;
    }

    const { tag, parsedMetaData } = tagEntry;

    result.push({
      tag: {
        category: tag.category ?? '',
        path: tag.path ?? '',
        metaData: parsedMetaData,
      },
      module: {
        slug,
        variant: mmt.moduleVariant,
      },
      metaData:
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- API can return null despite type definition
        mmt.metaData &&
        (mmt.metaData.displayOrder !== undefined ||
          mmt.metaData.difficultyLevel !== undefined)
          ? {
              DisplayOrder: mmt.metaData.displayOrder ?? undefined,
              DifficultyLevel: mmt.metaData.difficultyLevel ?? undefined,
            }
          : undefined,
    });
  }

  return [...result, ...buildSyntheticUnderConstructionTagging(modules)];
}

export function useModuleTagging(): ModuleTaggingResult {
  const { api, locale } = useFramework();
  const modules = useModules();

  const { data: taggingData, isLoading } = useSessionDataLoader<
    { moduleTaggingData?: ModuleTaggingData } & JsonObject,
    'moduleTaggingData'
  >('moduleTaggingData', async () => {
    const [moduleTagsResponse, moduleModuleTagsResponse] = await Promise.all([
      api.get_Modules_ModuleTags({}),
      api.get_Modules_ModuleModuleTags({ locale }),
    ]);

    return {
      moduleTags: moduleTagsResponse.items,
      moduleModuleTags: moduleModuleTagsResponse.items,
    };
  });

  // Transform the data only when all pieces are available
  const moduleTagging = useMemo(
    () =>
      taggingData && modules
        ? transformToModuleTagging(
            taggingData.moduleTags,
            taggingData.moduleModuleTags,
            modules,
          )
        : undefined,
    [taggingData, modules],
  );

  return {
    moduleTagging,
    moduleTags: taggingData?.moduleTags,
    isLoading: isLoading || !modules,
  };
}

/**
 * Returns the set of module slugs tagged as under construction (category
 * `Status`, path `under-construction`). Shared by the canvas hub, the catalog
 * and game library views, and the module page so they all agree on which games
 * are offline.
 */
export function buildUnderConstructionSet(
  moduleTagging: readonly ModuleTagging[],
): Set<string> {
  return new Set(
    moduleTagging
      .filter(
        (t) =>
          t.tag.category === MODULE_STATUS_CATEGORY &&
          t.tag.path === UNDER_CONSTRUCTION_TAG_PATH,
      )
      .map((t) => t.module.slug),
  );
}
